import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent } from "./ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Switch } from "./ui/switch";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";
import { Zap, Plus, Trash2, Edit, Loader2, Building2 } from "lucide-react";

export const WhatsAppQuickReplies = ({ isAdmin, getAuthHeaders }) => {
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ titulo: "", mensaje: "", is_active: true, department_id: "", department_name: "" });
  const [departamentos, setDepartamentos] = useState([]);

  useEffect(() => {
    axios.get(`${API}/departamentos`, getAuthHeaders()).then((res) => setDepartamentos(res.data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchReplies = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/whatsapp/quick-replies`, getAuthHeaders());
      setReplies(res.data || []);
    } catch (err) {
      toast.error("Error al cargar respuestas rápidas");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchReplies(); }, [fetchReplies]);

  const openNew = () => {
    setEditing(null);
    setForm({ titulo: "", mensaje: "", is_active: true, department_id: "", department_name: "" });
    setShowDialog(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({ titulo: r.titulo, mensaje: r.mensaje, is_active: r.is_active, department_id: r.department_id || "", department_name: r.department_name || "" });
    setShowDialog(true);
  };

  const handleGuardar = async () => {
    if (!form.titulo.trim() || !form.mensaje.trim()) {
      toast.error("El título y el mensaje son obligatorios");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/whatsapp/quick-replies/${editing.id}`, form, getAuthHeaders());
        toast.success("Respuesta rápida actualizada");
      } else {
        await axios.post(`${API}/whatsapp/quick-replies`, form, getAuthHeaders());
        toast.success("Respuesta rápida creada");
      }
      setShowDialog(false);
      fetchReplies();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (r, v) => {
    try {
      await axios.put(`${API}/whatsapp/quick-replies/${r.id}`, { titulo: r.titulo, mensaje: r.mensaje, is_active: v, department_id: r.department_id || null, department_name: r.department_name || null }, getAuthHeaders());
      fetchReplies();
    } catch (err) {
      toast.error("Error al actualizar");
    }
  };

  const handleEliminar = async (r) => {
    if (!window.confirm(`¿Eliminar la respuesta rápida "${r.titulo}"?`)) return;
    try {
      await axios.delete(`${API}/whatsapp/quick-replies/${r.id}`, getAuthHeaders());
      toast.success("Respuesta rápida eliminada");
      fetchReplies();
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="quick-replies-section">
      <Card className="border-amber-200 bg-amber-50/60 dark:bg-amber-900/10">
        <CardContent className="pt-5 pb-5 flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center shrink-0">
              <Zap className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="font-semibold text-amber-900 dark:text-amber-100">Respuestas rápidas para agentes</p>
              <p className="text-sm text-amber-700 dark:text-amber-300">
                Los agentes las insertan con el botón ⚡ junto al cuadro de mensaje en la Bandeja de WhatsApp. Solo el administrador puede crearlas o editarlas.
              </p>
            </div>
          </div>
          {isAdmin && (
            <Button onClick={openNew} className="bg-amber-500 hover:bg-amber-600 text-white shrink-0" data-testid="new-quick-reply-button">
              <Plus className="w-4 h-4 mr-2" />Nueva Respuesta Rápida
            </Button>
          )}
        </CardContent>
      </Card>

      {replies.length === 0 ? (
        <Card className="border-dashed border-2">
          <CardContent className="py-10 text-center text-slate-500">No hay respuestas rápidas configuradas.</CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {replies.map((r) => (
            <Card key={r.id} data-testid={`quick-reply-card-${r.id}`} className={!r.is_active ? "opacity-60" : ""}>
              <CardContent className="pt-4 pb-4 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                    <Zap className="w-4 h-4 text-amber-500" />{r.titulo}
                  </p>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs" data-testid={`quick-reply-depto-badge-${r.id}`}>
                      <Building2 className="w-3 h-3 mr-1" />{r.department_name || "Global"}
                    </Badge>
                    <Badge variant={r.is_active ? "default" : "secondary"} className={r.is_active ? "bg-emerald-500" : ""}>
                      {r.is_active ? "Activa" : "Inactiva"}
                    </Badge>
                    {isAdmin && <Switch checked={!!r.is_active} onCheckedChange={(v) => handleToggle(r, v)} data-testid={`quick-reply-toggle-${r.id}`} />}
                  </div>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-800 rounded-lg p-3 whitespace-pre-wrap">{r.mensaje}</p>
                {isAdmin && (
                  <div className="flex gap-2 pt-1">
                    <Button variant="outline" size="sm" onClick={() => openEdit(r)} data-testid={`quick-reply-edit-${r.id}`}>
                      <Edit className="w-4 h-4 mr-1" />Editar
                    </Button>
                    <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => handleEliminar(r)} data-testid={`quick-reply-delete-${r.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Zap className="w-5 h-5 text-amber-500" />{editing ? "Editar" : "Nueva"} Respuesta Rápida</DialogTitle>
            <DialogDescription>Texto listo para que los agentes lo inserten en la conversación.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Título</Label>
              <Input value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} placeholder="Ej. Saludo inicial" data-testid="quick-reply-titulo" />
            </div>
            <div>
              <Label>Mensaje</Label>
              <Textarea value={form.mensaje} onChange={(e) => setForm({ ...form, mensaje: e.target.value })} rows={4} placeholder="Texto que se insertará en el chat..." data-testid="quick-reply-mensaje" />
            </div>
            <div>
              <Label>Departamento</Label>
              <Select value={form.department_id || "global"} onValueChange={(v) => {
                if (v === "global") { setForm({ ...form, department_id: "", department_name: "" }); }
                else { const d = departamentos.find((x) => x.id === v); setForm({ ...form, department_id: v, department_name: d ? (d.name || d.nombre) : "" }); }
              }}>
                <SelectTrigger data-testid="quick-reply-departamento"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="global">🌐 Global (visible para todos)</SelectItem>
                  {departamentos.map((d) => <SelectItem key={d.id} value={d.id}>{d.name || d.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">Si eliges un departamento, solo los agentes de ese departamento la verán en la Bandeja.</p>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="quick-reply-form-activa" />
              <Label>Activa</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
            <Button onClick={handleGuardar} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="quick-reply-guardar">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WhatsAppQuickReplies;
